/**
 * Configuration des statuts et onglets pour les commandes (Prestataire)
 */

import { StatusActionConfig } from "./types";

// ============================================================================
// Définition des onglets
// ============================================================================

export const TABS = [
  "Tous",
  "Action requise",
  "En attente",
  // Onglet repurposé : ancien "Brouillons" -> "Chiffrage" (liste des chiffrages du prestataire,
  // alimentée par chiffrageService côté front, non plus par le mapping de statut ci-dessous).
  "Chiffrages",
  "Acceptés",
  "Refusé / Expiré",
  "Archive",
  "Paniers importés",
] as const;

export type TabName = (typeof TABS)[number];

// ============================================================================
// Mapping onglet → filtres API
// ============================================================================

export const TAB_TO_STATUS_MAPPING: Record<
  number,
  { filter: string; statusIds: number[] }
> = {
  0: { filter: "", statusIds: [] }, // Tous
  1: { filter: "status_action=1", statusIds: [] }, // Action requise
  2: {
    filter: "status_action=0&status_type_compte=Prestataire",
    statusIds: [],
  }, // En attente
  // Index 3 : onglet désormais "Chiffrage" (liste front, hors mapping de statut). Entrée CONSERVÉE
  // volontairement (ancien "Brouillons", status_id=1) comme garde-fou de bornes du mapping.
  3: { filter: "status_id=1", statusIds: [1] }, // (ancien "Brouillons")
  4: { filter: "status_id=11", statusIds: [11] }, // Acceptés
  5: { filter: "status_id=6,12", statusIds: [6, 12] }, // Refusé / Expiré
  6: { filter: "accepté", statusIds: [] }, // Archive
  7: { filter: "", statusIds: [] }, // Paniers importés
};

// ============================================================================
// Options de filtre par type
// ============================================================================

export const TYPE_OPTIONS = [
  { label: "Tous", value: "Tous" },
  { label: "Bon de Commande", value: "BC" },
  { label: "Marché Subséquent", value: "MS" },
];

// ============================================================================
// Mapping des actions par statut pour les Marchés Subséquents (MS)
// ============================================================================

export const STATUS_ACTION_MAP_MS: Record<number, StatusActionConfig> = {
  // Brouillon
  1: {
    buttonText: "Reprendre",
    action: "reprendre",
    forRoles: ["Admin", "Prestataire admin"],
    requiresAction: false,
  },
  // Candidature en cours
  2: {
    buttonText: "",
    action: "",
    forRoles: [],
    requiresAction: false,
  },
  // À candidater
  3: {
    buttonText: "À candidater",
    action: "candidater",
    forRoles: ["Prestataire", "Prestataire admin", "Admin"],
    requiresAction: true,
  },
  // En analyse
  4: {
    buttonText: "",
    action: "",
    forRoles: [],
    requiresAction: false,
  },
  // En attente de l'analyse
  5: {
    buttonText: "",
    action: "",
    forRoles: [],
    requiresAction: false,
  },
  // Refusé
  6: {
    buttonText: "",
    action: "",
    forRoles: [],
    requiresAction: false,
  },
  // En attente de votre signature (acheteur)
  7: {
    buttonText: "",
    action: "",
    forRoles: [],
    requiresAction: false,
  },
  // En attente décision
  8: {
    buttonText: "",
    action: "",
    forRoles: [],
    requiresAction: false,
  },
  // En attente de signature prestataire
  9: {
    buttonText: "",
    action: "",
    forRoles: [],
    requiresAction: false,
  },
  // En attente de votre signature (prestataire)
  10: {
    buttonText: "À signer",
    action: "signer",
    forRoles: ["Prestataire", "Prestataire admin", "Admin"],
    requiresAction: true,
  },
  // Validé
  11: {
    buttonText: "Marché subséquent",
    action: "download",
    forRoles: ["Prestataire", "Prestataire admin", "Admin"],
    requiresAction: true,
  },
  // Expiré
  12: {
    buttonText: "",
    action: "",
    forRoles: [],
    requiresAction: false,
  },
};

// ============================================================================
// Mapping des actions par statut pour les Bons de Commande (BC)
// ============================================================================

export const STATUS_ACTION_MAP_BC: Record<number, StatusActionConfig> = {
  // En attente de votre acceptation
  4: {
    buttonText: "À accepter",
    action: "accepter",
    forRoles: ["Prestataire", "Prestataire admin", "Admin"],
    requiresAction: true,
  },
  // Accepté
  7: {
    buttonText: "Bon de commande",
    action: "download",
    forRoles: ["Prestataire", "Prestataire admin", "Admin"],
    requiresAction: true,
  },
  // À chiffrer
  11: {
    buttonText: "À chiffrer",
    action: "chiffrer",
    forRoles: ["Prestataire", "Prestataire admin", "Admin"],
    requiresAction: true,
  },
  // En attente de l'analyse
  13: {
    buttonText: "En attente de l'analyse",
    action: "",
    forRoles: ["Prestataire", "Prestataire admin", "Admin"],
    requiresAction: true,
  },
  // À signature
  17: {
    buttonText: "À signature",
    action: "",
    forRoles: ["Prestataire", "Prestataire admin", "Admin"],
    requiresAction: true,
  },
};

// ============================================================================
// Mapping des actions par statut pour les Bons de Commande Importés (BCI)
// ============================================================================

export const STATUS_ACTION_MAP_BCI: Record<number, StatusActionConfig> = {
  // Validé (Accepté)
  7: {
    buttonText: "Bon de commande",
    action: "download",
    forRoles: ["Prestataire", "Prestataire admin", "Admin"],
    requiresAction: false,
  },
  // À valider
  11: {
    buttonText: "À envoyer à l'acheteur",
    action: "valider",
    forRoles: ["Prestataire", "Prestataire admin", "Admin"],
    requiresAction: true,
  },
};

// ============================================================================
// Statuts exclus du compte à rebours
// ============================================================================

export const COUNTDOWN_EXCLUDED_STATUS_IDS = [5, 6, 7, 8, 12];
